/**
 * Plain-language valuation story from AVM + optional area growth stats.
 * Steps: similar sales found → local growth context → estimate.
 */

const TIER_PLAIN = {
  sector_tight: 'the same postcode sector, closely matched on beds and size',
  sector_wide: 'the same postcode sector, with a wider size range',
  district_wide: 'the wider postcode district',
  district_wide_raw: 'the wider postcode district (including sales without a full EPC match)',
  nearby_district: 'a neighbouring district with similar homes',
  nearby_district_raw: 'a neighbouring district’s wider sales pool',
  cross_type_psqm: 'local £/sqm sales across property types',
  district_mean: 'the district average when direct matches were thin',
  none: 'available local sales',
};

function fmtGBP(n) {
  if (n == null) return null;
  return `£${Math.round(n).toLocaleString('en-GB')}`;
}

function fmtGrowth(pct) {
  if (pct == null || Number.isNaN(Number(pct))) return null;
  const p = Number(pct) * 100;
  const abs = Math.abs(p).toFixed(1);
  if (Math.abs(p) < 0.05) return 'little overall change';
  return p >= 0 ? `up about ${abs}%` : `down about ${abs}%`;
}

function formatType(t) {
  if (!t) return 'homes';
  return String(t).replace(/_/g, ' ').replace(/-/g, ' ');
}

/**
 * @param {object} opts
 * @param {object} opts.avm - find_comparables / desk valuation payload
 * @param {object} [opts.areaStats] - growth_pct_5yr / growth_pct_10yr etc.
 * @param {string} [opts.postcode]
 * @returns {{ title: string, steps: { label: string, text: string }[], estimateLine: string, tone: string } | null}
 */
export function buildValuationNarrative({ avm, areaStats = null, postcode = null } = {}) {
  if (!avm) return null;

  const count = avm.comparable_count || avm.comparables?.length || 0;
  const comps = avm.comparables || [];
  const type = formatType(avm.subject?.property_type);
  const beds = avm.subject?.bedrooms;
  const area = postcode || avm.subject?.postcode || 'this area';
  const district = typeof area === 'string' ? area.split(' ')[0] : 'this district';
  const tierKey = avm.tier_used || 'none';
  const where = TIER_PLAIN[tierKey] || TIER_PLAIN.none;
  const evidence = avm.evidence_tier || 'none';

  const examples = comps
    .slice(0, 3)
    .map((c) => {
      const addr = c.address || c.postcode || 'a nearby sale';
      const price = fmtGBP(c.sale_price);
      const when = c.sale_date ? String(c.sale_date).slice(0, 7) : null;
      return price ? `${addr} (${price}${when ? `, ${when}` : ''})` : addr;
    })
    .filter(Boolean);

  let foundText;
  if (count > 0) {
    const bedBit = beds != null ? `${beds}-bed ` : '';
    foundText = `I found ${count} similar ${bedBit}${type} sale${count === 1 ? '' : 's'} in ${where} over the last 24 months.`;
    if (examples.length) {
      foundText += ` Examples include ${examples.join('; ')}.`;
    }
  } else {
    foundText = `There weren’t enough closely matched recent sales, so I used ${where} for ${area}.`;
  }

  if (avm.search_widened_to) {
    foundText += ` The search was widened to include sales from ${avm.search_widened_to}.`;
  }

  const g5 = fmtGrowth(areaStats?.growth_pct_5yr);
  const g10 = fmtGrowth(areaStats?.growth_pct_10yr);
  let growthText = null;
  if (g5 || g10) {
    const parts = [];
    if (g5) parts.push(`${g5} over 5 years`);
    if (g10) parts.push(`${g10} over 10 years`);
    growthText = `I mapped local price movement in ${district}: prices are ${parts.join(', and ')}.`;
  } else {
    growthText = `I checked recent sale prices around ${district} to place this home in the current market.`;
  }

  const mid = fmtGBP(avm.estimate_mid);
  const low = fmtGBP(avm.estimate_low);
  const high = fmtGBP(avm.estimate_high);
  let estimateLine;
  if (mid && low && high) {
    estimateLine = `Indicative value: ${mid} (typical range ${low} – ${high}).`;
  } else if (mid) {
    estimateLine = `Indicative value: ${mid}.`;
  } else {
    estimateLine = 'Not enough local evidence to produce a firm estimate.';
  }

  const tone = evidence === 'evidenced' ? 'emerald' : evidence === 'inferred' ? 'amber' : 'default';

  return {
    title: 'How I reached this valuation',
    steps: [
      { label: 'Similar properties', text: foundText },
      { label: 'Growth over time', text: growthText },
      { label: 'Value estimate', text: estimateLine },
    ],
    estimateLine,
    tone,
  };
}
